import { FC, useContext, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  createStyles,
  Link,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import { map } from "lodash";

import { CustomDialog, DownloadButton } from "components";
import { TROLLEY_PAYMENT_PORTAL_URL } from "constants/index";
import { generateAssetDistributionPDF } from "containers/PDFStatementGenerator";
import { AppContext } from "contexts";
import axios, { makeSimpleGetRequest } from "lib/axios";
import { fileDownloadErrorMessage } from "lib/axios/constants";
import { formatAmount, formatHumanDate } from "lib/format";
import { parseNumber } from "lib/parse";
import { getAssetDistributionTaskDownloadURL } from "services/assetDistributionTasks";
import { useManyAssetAssetAdministrationDistributions } from "services/assets";
import { getAssetAdministrationDistributionsToDisplay } from "services/assets/assetsPostProcessor";
import { AssetAssetAdministrationDistribution } from "services/assets/types";
import { getFileNameFromContentDisposition } from "services/documents";
import theme from "theme";

import { SellerAssetAdministrationItem } from "../index";

const getPaymentCategory = (
  userId: number,
  assetAdministrationDistribution: AssetAssetAdministrationDistribution,
) => {
  const isAssetOwner =
    userId ===
    assetAdministrationDistribution.asset_administration.asset.seller;
  return {
    royaltiesEarned: isAssetOwner
      ? parseNumber(
          assetAdministrationDistribution.active_asset_distribution_task
            .net_payable,
        )
      : 0,
    royaltiesReceived: isAssetOwner
      ? parseNumber(
          assetAdministrationDistribution.active_asset_distribution_task
            .related_net_payable,
        )
      : 0,
    recoupmentAmount: !isAssetOwner
      ? parseNumber(
          assetAdministrationDistribution.active_asset_distribution_task
            .net_payable,
        )
      : 0,
  };
};

const useStyles = makeStyles(() =>
  createStyles({
    link: {
      color: theme.custom.palette.blue.main,
      fontSize: "inherit",
      "&:hover": {
        textDecoration: "underline",
      },
    },
    tableContainer: {
      overflowX: "unset",
    },
    table: {
      "& thead th": {
        borderBottom: `1px solid ${theme.custom.palette.gray.dark}`,
      },
      "& tbody td": {
        borderBottom: `1px solid ${theme.custom.palette.gray.dark}`,
      },
      "& tbody tr td:first-child": {
        borderLeft: `1px solid ${theme.custom.palette.gray.dark}`,
      },
      "& tbody tr td:last-child": {
        borderRight: `1px solid ${theme.custom.palette.gray.dark}`,
      },
    },
    distributionContainer: {
      padding: `${theme.spacing(0)}px ${theme.spacing(2)}px ${theme.spacing(
        3,
      )}px ${theme.spacing(2)}px`,
    },
    title: {
      fontSize: "2rem",
    },
    subtitle: {
      fontSize: "1.2rem",
      fontWeight: theme.typography.fontWeightBold,
    },
    smallText: {
      fontSize: "0.8rem",
      color: theme.custom.palette.gray.dark,
    },
    warning: {
      margin: `${theme.spacing(1)}px 0`,
      width: "100%",
    },
    alertInfo: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: theme.spacing(1),
      textAlign: "left",
    },
  }),
);

interface AssetAdministrationModalProps {
  onClose: () => void;
  sellerAssetAdministrationItem: SellerAssetAdministrationItem;
}

const AssetAdministrationModal: FC<AssetAdministrationModalProps> = ({
  sellerAssetAdministrationItem,
  onClose,
}) => {
  const isAdvance =
    sellerAssetAdministrationItem.purchased_term == "fixed_return";
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const classes = useStyles();
  const appContext = useContext(AppContext);
  const userId = appContext.user?.userId;

  const assetAssetAdministrationDistributionsQueries = useManyAssetAssetAdministrationDistributions(
    map(
      sellerAssetAdministrationItem.asset_administrations,
      asset_administration => asset_administration.asset_id,
    ),
  );
  const isAssetAssetAdministrationDistributionsQueriesLoading = assetAssetAdministrationDistributionsQueries.some(
    query => query.isLoading,
  );
  const assetAdministrationDistributions = !isAssetAssetAdministrationDistributionsQueriesLoading
    ? assetAssetAdministrationDistributionsQueries.flatMap(query => query.data)
    : [];
  const assetAdministrationDistributionsWithPaymentStatus = assetAdministrationDistributions.filter(
    assetAssetAdministrationDistribution =>
      assetAssetAdministrationDistribution.payment_status,
  );
  const assetAdministrationDistributionsToDisplay = getAssetAdministrationDistributionsToDisplay(
    assetAdministrationDistributions,
  );

  const [downloadErrorMessage, setDownloadErrorMessage] = useState("");

  const handleAssetDistributionCSVDownload = async (
    assetDistributionTaskId: number,
  ) => {
    try {
      setDownloadErrorMessage("");
      const response = await axios.get(
        getAssetDistributionTaskDownloadURL(assetDistributionTaskId, true),
      );
      const fileName = getFileNameFromContentDisposition(
        response.headers["content-disposition"],
      );
      saveAs(new Blob([response.data]), fileName);
    } catch (e) {
      setDownloadErrorMessage(fileDownloadErrorMessage);
    }
  };

  const handleAssetDistributionPdfDownload = async (
    assetDistributionTaskId: number,
  ) => {
    try {
      setDownloadErrorMessage("");
      const assetDistributionJSON: any = await makeSimpleGetRequest(
        getAssetDistributionTaskDownloadURL(assetDistributionTaskId, false),
      );
      const { fileName, pdfFile } = generateAssetDistributionPDF(
        assetDistributionJSON || null,
        sellerAssetAdministrationItem.purchased_term,
        assetDistributionJSON && "owner_is_buyer" in assetDistributionJSON
          ? !(assetDistributionJSON?.owner_is_buyer as boolean)
          : false,
      );
      pdfFile.save(fileName);
    } catch (e) {
      setDownloadErrorMessage(fileDownloadErrorMessage);
    }
  };

  return (
    <CustomDialog onClose={onClose}>
      <Box className={classes.distributionContainer}>
        <Typography variant="h1" className={classes.title}>
          {sellerAssetAdministrationItem.deal_title ?? "Deal Details"}
        </Typography>
        <Box my={3} display="flex" justifyContent="space-between">
          {sellerAssetAdministrationItem.previous_hubspot_deal_id ? (
            sellerAssetAdministrationItem.deal_url ? (
              <Link
                href={sellerAssetAdministrationItem.deal_url}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                className={classes.link}
              >
                Deal&nbsp;#
                {sellerAssetAdministrationItem.previous_hubspot_deal_id}
              </Link>
            ) : (
              <>
                Deal&nbsp;#
                {sellerAssetAdministrationItem.previous_hubspot_deal_id}
              </>
            )
          ) : (
            <Box />
          )}
          <Typography className={classes.smallText}>
            Last updated on{" "}
            {formatHumanDate(sellerAssetAdministrationItem.last_update)}
          </Typography>
        </Box>
        <Box>
          <Typography className={classes.subtitle} gutterBottom>
            Payments
          </Typography>
          {!!assetAdministrationDistributionsWithPaymentStatus.length && (
            <Alert severity="warning" className={classes.warning}>
              <AlertTitle>Payment Status Update</AlertTitle>
              {assetAdministrationDistributionsWithPaymentStatus.map(
                assetAdministrationDistribution =>
                  assetAdministrationDistribution.payment_status ===
                  "Trolley recipient is inactive" ? (
                    <Typography
                      variant="body1"
                      key={
                        assetAdministrationDistribution.payment_status_updated_at
                      }
                    >
                      {formatHumanDate(
                        assetAdministrationDistribution.payment_status_updated_at,
                      )}
                      &nbsp;-&nbsp;
                      <>
                        Please complete your account setup in the{" "}
                        <Link
                          className={classes.link}
                          href={TROLLEY_PAYMENT_PORTAL_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Payment Portal
                        </Link>{" "}
                        to receive payment.
                      </>
                    </Typography>
                  ) : (
                    <Typography
                      variant="body1"
                      key={assetAdministrationDistribution.payment_status}
                    >
                      {formatHumanDate(
                        assetAdministrationDistribution.payment_status_updated_at,
                      )}
                      &nbsp;-&nbsp;
                      {assetAdministrationDistribution.payment_status}
                    </Typography>
                  ),
              )}
            </Alert>
          )}
          {!!downloadErrorMessage && (
            <Alert severity="error" className={classes.warning}>
              <AlertTitle>Download Error</AlertTitle>
              <Typography variant="body1">{downloadErrorMessage}</Typography>
            </Alert>
          )}
          {isAssetAssetAdministrationDistributionsQueriesLoading && (
            <CircularProgress />
          )}
          {!isAssetAssetAdministrationDistributionsQueriesLoading && (
            <>
              {assetAdministrationDistributionsToDisplay.length ? (
                <>
                  {isSmDown ? (
                    <Card>
                      <CardContent>
                        {assetAdministrationDistributionsToDisplay.map(
                          (assetAdministrationDistribution, i) => {
                            const isLastRow =
                              i ===
                              assetAdministrationDistributionsToDisplay.length -
                                1;
                            const {
                              recoupmentAmount,
                              royaltiesEarned,
                              royaltiesReceived,
                            } = getPaymentCategory(
                              userId,
                              assetAdministrationDistribution,
                            );
                            return (
                              <Box
                                mb={isLastRow ? 0 : 2}
                                key={assetAdministrationDistribution.id}
                              >
                                <Box width="100%" fontWeight="bold" mb={1}>
                                  {
                                    assetAdministrationDistribution.distribution
                                      .display_name
                                  }
                                </Box>
                                {isAdvance ? (
                                  <>
                                    <Box width="100%">
                                      <Typography>
                                        <strong>Recoupment:</strong>{" "}
                                        {formatAmount(recoupmentAmount)}
                                      </Typography>
                                    </Box>
                                    <Box width="100%" mb={1}>
                                      <Typography>
                                        <strong>Royalties Received:</strong>{" "}
                                        {formatAmount(royaltiesReceived)}
                                      </Typography>
                                    </Box>
                                  </>
                                ) : (
                                  <>
                                    <Box width="100%">
                                      <Typography>
                                        <strong>Amount:</strong>{" "}
                                        {formatAmount(royaltiesEarned)}
                                      </Typography>
                                    </Box>
                                    <Box width="100%" mb={1}>
                                      <Typography>
                                        <strong>Royalties Received:</strong>{" "}
                                        {formatAmount(royaltiesReceived)}
                                      </Typography>
                                    </Box>
                                  </>
                                )}
                                <Box
                                  display="flex"
                                  width="100%"
                                  justifyContent="space-around"
                                  mb={isLastRow ? 0 : 1}
                                >
                                  <DownloadButton
                                    kind="pdf"
                                    handleDownload={() =>
                                      handleAssetDistributionPdfDownload(
                                        assetAdministrationDistribution
                                          .active_asset_distribution_task.id,
                                      )
                                    }
                                  />
                                  <DownloadButton
                                    kind="csv"
                                    handleDownload={() =>
                                      handleAssetDistributionCSVDownload(
                                        assetAdministrationDistribution
                                          .active_asset_distribution_task.id,
                                      )
                                    }
                                  />
                                </Box>
                                {!isLastRow && <hr />}
                              </Box>
                            );
                          },
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <TableContainer className={classes.tableContainer}>
                      <Table className={classes.table}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Distribution</TableCell>
                            {isAdvance ? (
                              <>
                                <TableCell align="right">
                                  Recoupment Amount
                                </TableCell>
                                <TableCell align="right">
                                  Royalties Received
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell align="right">
                                  Royalties Received
                                </TableCell>
                              </>
                            )}
                            <TableCell align="right">Downloads</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {assetAdministrationDistributionsToDisplay.map(
                            (assetAdministrationDistribution, index) => {
                              const {
                                recoupmentAmount,
                                royaltiesEarned,
                                royaltiesReceived,
                              } = getPaymentCategory(
                                userId,
                                assetAdministrationDistribution,
                              );
                              return (
                                <TableRow key={index}>
                                  <TableCell align="left">
                                    {
                                      assetAdministrationDistribution
                                        .distribution.display_name
                                    }
                                  </TableCell>
                                  {isAdvance ? (
                                    <>
                                      <TableCell align="right">
                                        <Typography>
                                          {formatAmount(recoupmentAmount)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography>
                                          {formatAmount(royaltiesReceived)}
                                        </Typography>
                                      </TableCell>
                                    </>
                                  ) : (
                                    <>
                                      <TableCell align="right">
                                        <Typography>
                                          {formatAmount(royaltiesEarned)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography>
                                          {formatAmount(royaltiesReceived)}
                                        </Typography>
                                      </TableCell>
                                    </>
                                  )}
                                  <TableCell align="right">
                                    <Box
                                      mr={-2}
                                      minWidth="96px"
                                      display="flex"
                                      justifyContent="flex-end"
                                    >
                                      <DownloadButton
                                        kind="pdf"
                                        handleDownload={() =>
                                          handleAssetDistributionPdfDownload(
                                            assetAdministrationDistribution
                                              .active_asset_distribution_task
                                              .id,
                                          )
                                        }
                                      />
                                      <DownloadButton
                                        kind="csv"
                                        handleDownload={() =>
                                          handleAssetDistributionCSVDownload(
                                            assetAdministrationDistribution
                                              .active_asset_distribution_task
                                              .id,
                                          )
                                        }
                                      />
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            },
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  <Box mt={1}>
                    <Typography variant="body1">
                      <strong>Note:</strong> Statements paid prior to 2023 are
                      not available.
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box mt={1}>
                  <Typography>
                    No payments processed. Statements will be available here
                    once payments are processed.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
      <div
        style={{
          width: 600,
          height: 400,
          overflow: "hidden",
          position: "absolute",
          top: -400,
          left: -600,
        }}
      >
        <canvas id="pdf-report-canvas" width="600" height="400"></canvas>
      </div>
    </CustomDialog>
  );
};

export default AssetAdministrationModal;
